import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { DonationPage, Donation } from '../types';

interface DonationPageManagerProps {
  page: DonationPage;
  onBack: () => void;
}

const DonationPageManager: React.FC<DonationPageManagerProps> = ({ page, onBack }) => {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [editing, setEditing] = useState<boolean>(false);
  const [minAmount, setMinAmount] = useState<number>(page.min_amount);
  const [description, setDescription] = useState<string>(page.description);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    loadDonations();
  }, [page.id]);

  const loadDonations = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDonations(page.id);
      
      if (response.success && response.data) {
        setDonations(response.data);
      } else {
        setError('Failed to load donations');
      }
    } catch (err) {
      setError('Failed to load donations');
      console.error('Error loading donations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await apiService.updateDonationPage(page.id, {
        min_amount: minAmount,
        description: description,
      });

      if (response.success) {
        setEditing(false);
        // Update local state
        page.min_amount = minAmount;
        page.description = description;
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to update donation page');
      console.error('Error updating page:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setMinAmount(page.min_amount);
    setDescription(page.description);
    setEditing(false);
    setError('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount: number) => {
    return amount.toFixed(4);
  };

  const truncateAddress = (address: string) => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const totalDonations = donations.reduce((sum, donation) => sum + donation.amount, 0);

  return (
    <div className="min-h-screen" style={{ paddingTop: '48px', paddingBottom: '32px' }}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="card mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {page.description || 'Donation Page'}
              </h1>
              <p className="text-gray-400">Page ID: {page.id}</p>
            </div>
            <button
              onClick={onBack}
              className="btn-ghost"
            >
              ← Back to Pages
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="stat-card">
              <div className="stat-value">{formatAmount(totalDonations)}</div>
              <div className="stat-label">Total SOL Received</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{donations.length}</div>
              <div className="stat-label">Total Donations</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{formatAmount(page.min_amount)}</div>
              <div className="stat-label">Min Amount (SOL)</div>
            </div>
          </div>

          {/* Settings */}
          <div className="border-t border-gray-700 pt-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Page Settings</h3>
              {!editing ? (
                <button
                  onClick={() => setEditing(true)}
                  className="btn-primary"
                >
                  Edit Settings
                </button>
              ) : (
                <div className="flex space-x-3">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {editing ? (
              <div className="space-y-6">
                <div>
                  <label className="block text-lg font-semibold text-white mb-3">
                    Minimum Donation (SOL)
                  </label>
                  <input
                    type="number"
                    step="0.001"
                    min="0.001"
                    value={minAmount}
                    onChange={(e) => setMinAmount(parseFloat(e.target.value))}
                    className="input-field text-lg"
                  />
                </div>
                <div>
                  <label className="block text-lg font-semibold text-white mb-3">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="input-field text-lg resize-none"
                    placeholder="Describe what you're raising funds for..."
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4 text-lg">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Minimum Amount:</span>
                  <span className="text-purple-400 font-semibold">
                    {formatAmount(page.min_amount)} SOL
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Description:</span>
                  <span className="text-white font-medium">
                    {page.description || 'No description'}
                  </span>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 bg-red-500/20 border border-red-500/50 rounded-lg p-4">
                <p className="text-red-400">{error}</p>
              </div>
            )}
          </div>

          {/* URLs */}
          <div className="border-t border-gray-700 pt-6 mt-6">
            <h3 className="text-2xl font-bold text-white mb-4">Page URLs</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <input
                  type="text"
                  value={`${window.location.origin}/donate/${page.id}`}
                  readOnly
                  className="flex-1 input-field text-sm"
                />
                <button
                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/donate/${page.id}`)}
                  className="btn-secondary btn-sm"
                >
                  Copy
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <input
                  type="text"
                  value={`${window.location.origin}/obs/${page.id}`}
                  readOnly
                  className="flex-1 input-field text-sm"
                />
                <button
                  onClick={() => navigator.clipboard.writeText(`${window.location.origin}/obs/${page.id}`)}
                  className="btn-primary btn-sm"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Donations History */}
        <div className="card">
          <h3 className="text-2xl font-bold text-white mb-6">Recent Donations</h3>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="spinner mx-auto mb-4"></div>
              <p className="text-gray-300">Loading donations...</p>
            </div>
          ) : donations.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 text-gray-500">💰</div>
              <h4 className="text-xl font-semibold text-gray-400 mb-2">No donations yet</h4>
              <p className="text-gray-500">Share your donation page to start receiving SOL</p>
            </div>
          ) : (
            <div className="space-y-4">
              {donations.map((donation) => (
                <div key={donation.id} className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl font-bold text-green-400">
                        {formatAmount(donation.amount)} SOL
                      </span>
                      <span className="status-confirmed">
                        {donation.status}
                      </span>
                    </div>
                    <span className="text-sm text-gray-400">
                      {formatDate(donation.created_at)}
                    </span>
                  </div>
                  
                  {donation.message && (
                    <p className="text-gray-300 mb-4 text-lg">"{donation.message}"</p>
                  )}
                  
                  <div className="flex justify-between items-center text-sm text-gray-400">
                    <span>From: {truncateAddress(donation.from_address)}</span>
                    <span className="font-mono text-xs">
                      {donation.tx_hash.slice(0, 8)}...
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonationPageManager;